import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  IconButton,
  Grid,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogContentText,
  DialogActions,
  useTheme,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  AutoAwesome,
  Download,
  Share,
  Image as ImageIcon,
  History,
  InfoOutlined,
  Bolt,
  Lock,
  Star,
  Close,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import axios from "axios";
import API_BASE_URL from "../config";
import { db, onAuthChange } from "../firebase";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";

interface ImageHistoryItem {
  prompt: string;
  images: string[];
  timestamp: string;
}

export default function ImageGenerator(): JSX.Element {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState<string[]>([]);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  // Auth & DB logic
  const [user, setUser] = useState<any>(null);
  const [authInitialized, setAuthInitialized] = useState(false);

  // Usage Limit Logic
  const LIMIT = 5000; // Increased from 3
  const [usageCount, setUsageCount] = useState(0);

  // History Logic
  const [history, setHistory] = useState<ImageHistoryItem[]>([]);

  useEffect(() => {
    const unsubscribe = onAuthChange(async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        // Load from Firebase
        try {
          const userRef = doc(db, "users", currentUser.uid);
          const snap = await getDoc(userRef);
          if (snap.exists()) {
            const data = snap.data();
            if (data.imageGenCount !== undefined)
              setUsageCount(data.imageGenCount);
            if (data.imageGenHistory !== undefined)
              setHistory(data.imageGenHistory);
          }
        } catch (error) {
          console.error("Failed to load user data from Firebase:", error);
        }
      } else {
        // Clear state if logged out
        setUsageCount(0);
        setHistory([]);
      }
      setAuthInitialized(true);
    });

    return () => unsubscribe();
  }, []);

  const [showProModal, setShowProModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [previewImages, setPreviewImages] = useState<string[] | null>(null);
  const [previewIndex, setPreviewIndex] = useState(0);

  const [isInstagramMode, setIsInstagramMode] = useState(false);
  const isLimitReached = usageCount >= LIMIT;

  const handleGenerate = async () => {
    // 1. Enforce Login first
    if (!user) {
      setShowLoginModal(true);
      return;
    }

    // 2. Enforce Limit - Removed as requested
    /*
    if (isLimitReached) {
      setShowProModal(true);
      return;
    }
    */

    if (!prompt.trim()) return;
    setLoading(true);
    setImages([]); // Clear previous
    try {
      const response = await axios.post(`${API_BASE_URL}/generate-image`, {
        description: prompt,
        style: "realistic",
        num_images: 4,
        mode: isInstagramMode ? "instagram_carousel" : "standard"
      }, { timeout: 90000 });

      if (response.data && response.data.data) {
        const urls = response.data.data.map((img: any) => img.url);
        setImages(urls);

        // Update history
        const newHistoryItem: ImageHistoryItem = {
          prompt,
          images: urls,
          timestamp: new Date().toISOString(),
        };
        const updatedHistory = [newHistoryItem, ...history].slice(0, 50); // keep last 50
        setHistory(updatedHistory);

        // Increment usage
        const newCount = usageCount + 1;
        setUsageCount(newCount);

        // Sync with Firebase logic
        try {
          const userRef = doc(db, "users", user.uid);
          await setDoc(
            userRef,
            {
              imageGenCount: newCount,
              imageGenHistory: updatedHistory,
            },
            { merge: true },
          );
        } catch (e) {
          console.error("Failed to save to Firebase:", e);
        }
      }
    } catch (error) {
      console.error("Failed to generate images:", error);
      // Mock for UI demonstration if API fails or for speed
      // setImages([
      //   'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe',
      //   'https://images.unsplash.com/photo-1618005192384-a83a8bd57fbe',
      //   'https://images.unsplash.com/photo-1618005192384-a83a8bd57fbe',
      //   'https://images.unsplash.com/photo-1618005192384-a83a8bd57fbe'
      // ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", pt: 4 }}>
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 600,
            mb: 1,
            trackingTight: "-0.02em",
            color: isDarkMode ? "white" : "text.primary",
          }}
        >
          Image{" "}
          <span style={{ color: isDarkMode ? "#4b5563" : "#3b82f6" }}>
            Creator
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: isDarkMode ? "#9ca3af" : "text.secondary",
            fontWeight: 400,
            maxWidth: 600,
          }}
        >
          Generate professional figures, diagrams and illustrations for your
          research using AI.
        </Typography>
      </Box>

      <Dialog
        open={showProModal}
        onClose={() => setShowProModal(false)}
        PaperProps={{
          sx: {
            bgcolor: isDarkMode ? "#1e293b" : "background.paper",
            color: isDarkMode ? "white" : "text.primary",
            borderRadius: "24px",
            border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
            p: 2,
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Star sx={{ color: "#ec4899" }} /> Upgrade to Pro
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{ color: isDarkMode ? "#cbd5e1" : "text.secondary" }}
          >
            Upgrade to CoWrite Pro for unlimited high-resolution image generation,
            faster rendering speeds, and access to all professional styles.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowProModal(false)}
            sx={{ color: isDarkMode ? "#94a3b8" : "text.disabled" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              setShowProModal(false);
              (window as any).navigateToPage?.("premium");
            }}
            sx={{ borderRadius: "8px", fontWeight: 600 }}
          >
            Get Pro Access
          </Button>
        </DialogActions>
      </Dialog>

      {/* Login Modal */}
      <Dialog
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        PaperProps={{
          sx: {
            bgcolor: isDarkMode ? "#1e293b" : "background.paper",
            color: isDarkMode ? "white" : "text.primary",
            borderRadius: "24px",
            border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
            p: 2,
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Lock sx={{ color: "#3b82f6" }} /> Sign In Required
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            sx={{ color: isDarkMode ? "#cbd5e1" : "text.secondary" }}
          >
            Please sign in to your account to use the professional image
            generator and store your generated figures forever.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowLoginModal(false)}
            sx={{ color: isDarkMode ? "#94a3b8" : "text.disabled" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setShowLoginModal(false);
              (window as any).navigateToPage?.("signin");
            }}
            sx={{ borderRadius: "8px", fontWeight: 600 }}
          >
            Sign In Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fullscreen Image Modal Carousel */}
      <Dialog
        open={!!previewImages}
        onClose={() => {
          setPreviewImages(null);
          setPreviewIndex(0);
        }}
        maxWidth="lg"
        PaperProps={{
          sx: {
            bgcolor: "transparent",
            boxShadow: "none",
            overflow: "visible",
          },
        }}
      >
        {previewImages && previewImages.length > 0 && (
          <Box
            sx={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <IconButton
              onClick={() => {
                setPreviewImages(null);
                setPreviewIndex(0);
              }}
              sx={{
                position: "absolute",
                top: { xs: -40, md: -16 },
                right: { xs: 0, md: -48 },
                color: "white",
                bgcolor: "rgba(255,255,255,0.1)",
                backdropFilter: "blur(4px)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                zIndex: 10,
              }}
            >
              <Close />
            </IconButton>

            {previewImages.length > 1 && (
              <>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewIndex((prev) =>
                      prev > 0 ? prev - 1 : previewImages.length - 1,
                    );
                  }}
                  sx={{
                    position: "absolute",
                    left: { xs: 10, md: -60 },
                    color: "white",
                    bgcolor: "rgba(255,255,255,0.1)",
                    backdropFilter: "blur(4px)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                    zIndex: 10,
                  }}
                >
                  <ChevronLeft fontSize="large" />
                </IconButton>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    setPreviewIndex((prev) =>
                      prev < previewImages.length - 1 ? prev + 1 : 0,
                    );
                  }}
                  sx={{
                    position: "absolute",
                    right: { xs: 10, md: -60 },
                    color: "white",
                    bgcolor: "rgba(255,255,255,0.1)",
                    backdropFilter: "blur(4px)",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
                    zIndex: 10,
                  }}
                >
                  <ChevronRight fontSize="large" />
                </IconButton>
              </>
            )}

            <Box
              component="img"
              src={previewImages[previewIndex]}
              sx={{
                maxWidth: "100%",
                maxHeight: "90vh",
                objectFit: "contain",
                borderRadius: "16px",
                boxShadow: "0 24px 50px rgba(0,0,0,0.5)",
              }}
            />
          </Box>
        )}
      </Dialog>

      <Grid container spacing={4}>
        {/* Left Side: Image Canvas */}
        <Grid item xs={12} md={7}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 2,
              minHeight: 500,
            }}
          >
            {loading ? (
              <Box
                sx={{
                  gridColumn: "span 2",
                  bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
                  borderRadius: "24px",
                  border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                <CircularProgress size={40} sx={{ color: "#3b82f6" }} />
                <Typography
                  variant="body2"
                  sx={{ color: isDarkMode ? "#4b5563" : "text.secondary" }}
                >
                  Dreaming up your images...
                </Typography>
              </Box>
            ) : images.length > 0 ? (
              images.map((url, i) => (
                <Paper
                  key={i}
                  elevation={0}
                  sx={{
                    borderRadius: "20px",
                    bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
                    border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                    aspectRatio: "1",
                    overflow: "hidden",
                    position: "relative",
                    "&:hover .overlay": { opacity: 1 },
                  }}
                >
                  <Box
                    component="img"
                    src={url}
                    onClick={() => {
                      setPreviewImages(images);
                      setPreviewIndex(i);
                    }}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      cursor: "zoom-in",
                    }}
                  />
                  <Box
                    className="overlay"
                    onClick={() => {
                      setPreviewImages(images);
                      setPreviewIndex(i);
                    }}
                    sx={{
                      position: "absolute",
                      inset: 0,
                      bgcolor: "rgba(0,0,0,0.4)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      opacity: 0,
                      transition: "opacity 0.2s",
                      cursor: "zoom-in",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        bgcolor: "white",
                        color: "black",
                        "&:hover": { bgcolor: "#e5e7eb" },
                      }}
                    >
                      <Download fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        bgcolor: "white",
                        color: "black",
                        "&:hover": { bgcolor: "#e5e7eb" },
                      }}
                    >
                      <Share fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              ))
            ) : (
              <Box
                sx={{
                  gridColumn: "span 2",
                  bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
                  borderRadius: "24px",
                  border: `1px dashed ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.1)"}`,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: 0.3,
                }}
              >
                <ImageIcon
                  sx={{
                    fontSize: 64,
                    mb: 2,
                    color: isDarkMode ? "white" : "text.primary",
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: isDarkMode ? "white" : "text.primary" }}
                >
                  Generated images will appear in a grid
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>

        {/* Right Side: Controls */}
        <Grid item xs={12} md={5}>
          <Stack spacing={3}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "24px",
                bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
                border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: isDarkMode ? "white" : "text.primary",
                    fontWeight: 600,
                  }}
                >
                  Describe your figure
                </Typography>
                <FormControlLabel
                  control={
                    <Switch 
                      size="small" 
                      checked={isInstagramMode} 
                      onChange={(e) => setIsInstagramMode(e.target.checked)}
                      color="secondary"
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ fontWeight: 600, color: isInstagramMode ? "#ec4899" : "text.secondary" }}>
                      Instagram Mode
                    </Typography>
                  }
                />
              </Box>
              <TextField
                fullWidth
                multiline
                rows={5}
                placeholder="e.g., A professional 3D diagram showing the process of cell division, minimalist style, academic colors..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.03)"
                      : "rgba(0,0,0,0.02)",
                    borderRadius: "16px",
                    p: 2,
                    color: isDarkMode ? "white" : "text.primary",
                    fontSize: "14px",
                    lineHeight: 1.6,
                  },
                }}
              />
              <Button
                fullWidth
                variant="contained"
                onClick={handleGenerate}
                disabled={loading || !prompt.trim()}
                startIcon={<Bolt />}
                sx={{
                  mt: 3,
                  py: 1.5,
                  borderRadius: "12px",
                  bgcolor: !user
                    ? isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.05)"
                    : isDarkMode
                      ? "white"
                      : "primary.main",
                  color: !user
                    ? isDarkMode
                      ? "white"
                      : "black"
                    : isDarkMode
                      ? "black"
                      : "white",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": {
                    bgcolor: !user
                      ? isDarkMode
                        ? "rgba(255,255,255,0.2)"
                        : "rgba(0,0,0,0.1)"
                      : isDarkMode
                        ? "#e5e7eb"
                        : "primary.dark",
                  },
                  "&.Mui-disabled": {
                    bgcolor: isDarkMode
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.05)",
                    color: isDarkMode
                      ? "rgba(255,255,255,0.3)"
                      : "rgba(0,0,0,0.25)",
                  },
                }}
              >
                {!user ? "Sign In to Generate" : "Generate 4 Images"}
              </Button>
            </Paper>

            {/* Usage Counter */}
            <Box
              sx={{
                px: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="caption" sx={{ color: "#9ca3af" }}>
                Usage: {usageCount} generations
              </Typography>
            </Box>

            <Paper
              sx={{
                p: 3,
                borderRadius: "24px",
                bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
                border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: isDarkMode ? "white" : "text.primary",
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <History fontSize="small" sx={{ color: "#3b82f6" }} /> Quick
                Presets
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {[
                  "Diagram",
                  "Realistic",
                  "Illustration",
                  "3D Render",
                  "Minimalist",
                  "X-Ray",
                  "Blueprint",
                ].map((tag) => (
                  <Box
                    key={tag}
                    onClick={() =>
                      setPrompt((prev) => (prev ? `${prev}, ${tag}` : tag))
                    }
                    sx={{
                      px: 2,
                      py: 0.75,
                      borderRadius: "10px",
                      bgcolor: isDarkMode
                        ? "rgba(255,255,255,0.03)"
                        : "rgba(0,0,0,0.02)",
                      border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
                      color: isDarkMode ? "#9ca3af" : "text.secondary",
                      fontSize: "12px",
                      cursor: "pointer",
                      "&:hover": {
                        bgcolor: isDarkMode
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.06)",
                        color: isDarkMode ? "white" : "text.primary",
                      },
                    }}
                  >
                    {tag}
                  </Box>
                ))}
              </Box>
            </Paper>

            <Box sx={{ px: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: isDarkMode ? "#4b5563" : "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <InfoOutlined fontSize="inherit" />
                All images are generated in high resolution 1024x768.
              </Typography>
            </Box>
          </Stack>
        </Grid>
      </Grid>

      {/* History Section */}
      {history.length > 0 && (
        <Box sx={{ mt: 8, mb: 4 }}>
          <Typography
            variant="h5"
            sx={{
              mb: 3,
              fontWeight: 600,
              color: isDarkMode ? "white" : "text.primary",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <History sx={{ color: "#3b82f6" }} /> Your Generation History
          </Typography>
          <Grid container spacing={3}>
            {history.map((item, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Paper
                  sx={{
                    p: 2,
                    borderRadius: "20px",
                    bgcolor: isDarkMode ? "#0a0a0a" : "background.paper",
                    border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.06)"}`,
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: isDarkMode
                        ? "0 10px 40px -10px rgba(0,0,0,0.5)"
                        : "0 10px 40px -10px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={item.images[0]} // preview first image
                    onClick={() => {
                      setPreviewImages(item.images);
                      setPreviewIndex(0);
                    }}
                    sx={{
                      width: "100%",
                      aspectRatio: "1",
                      objectFit: "cover",
                      borderRadius: "12px",
                      mb: 2,
                      cursor: "zoom-in",
                      border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: isDarkMode ? "#cbd5e1" : "text.secondary",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      minHeight: "40px", // align heights
                      mb: 1,
                    }}
                    title={item.prompt}
                  >
                    "{item.prompt}"
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: isDarkMode ? "#64748b" : "#94a3b8" }}
                    >
                      {new Date(item.timestamp).toLocaleDateString()}
                    </Typography>
                    {item.images.length > 1 && (
                      <Typography
                        variant="caption"
                        sx={{
                          bgcolor: isDarkMode
                            ? "rgba(255,255,255,0.1)"
                            : "rgba(0,0,0,0.05)",
                          px: 1,
                          py: 0.5,
                          borderRadius: "6px",
                          color: isDarkMode ? "#9ca3af" : "text.secondary",
                          fontWeight: 600,
                        }}
                      >
                        +{item.images.length - 1}
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
}
